/*
 * Support hub — three-tab page where players can:
 *   1. Watch live server status / auto-restart schedule
 *   2. Report another player (web-only or Discord-linked both fine)
 *   3. Open a support ticket and continue threaded conversation with admins
 *
 * Routing: mounted at /support behind RequireAuth so the Status tab loads
 * the same way for everyone, but Report + Ticket forms require an active
 * session. The Status tab itself uses the PUBLIC endpoint
 * (/api/support/server-status) so unauthenticated users could in theory
 * see it — we still gate the whole page behind RequireAuth to keep nav
 * consistent.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Check,
  Flag,
  HelpCircle,
  LifeBuoy,
  MessageSquare,
  Send,
  Server,
  Shield,
  Wifi,
  WifiOff,
} from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";

const TICKET_CATEGORIES = [
  { value: "bug",        label: "Bug" },
  { value: "feature",    label: "Feature request" },
  { value: "account",    label: "Account / Login" },
  { value: "lost_item",  label: "Lost Pal / Item" },
  { value: "other",      label: "Other" },
];

const REPORT_REASONS = [
  "Cheating / Exploit",
  "Griefing / Base raid abuse",
  "Harassment / Chat toxicity",
  "Stealing / Theft",
  "Other",
];

const STATUS_COLOURS = {
  open:        "text-emerald-300 bg-emerald-300/10 border-emerald-300/30",
  in_progress: "text-amber-300 bg-amber-300/10 border-amber-300/30",
  resolved:    "text-sky-300 bg-sky-300/10 border-sky-300/30",
  closed:      "text-[var(--muted)] bg-white/5 border-white/10",
  pending:     "text-amber-300 bg-amber-300/10 border-amber-300/30",
};

function StatusPill({ status }) {
  const cls = STATUS_COLOURS[status] || STATUS_COLOURS.closed;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${cls}`}>
      {status?.replace("_", " ") || "—"}
    </span>
  );
}

function formatUptime(seconds) {
  if (!seconds) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const delta = Math.floor((Date.now() - t) / 1000);
  if (delta < 60) return `${delta}s ago`;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Tab: Server Status
// ---------------------------------------------------------------------------
function StatusTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = () => {
    api.get("/support/server-status")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  if (loading && !data) return <div className="text-sm text-[var(--muted)]">Loading server status…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="support-status-tab">
      <section>
        <h3 className="text-sm uppercase tracking-wider text-[var(--muted)] mb-3 flex items-center gap-2">
          <Server size={14} className="text-[var(--teal)]" />
          Live Servers
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.servers?.length ? data.servers.map((s) => (
            <article
              key={s.slug}
              className="glass-card p-4 space-y-1"
              data-testid={`status-server-${s.slug}`}
            >
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold truncate">{s.name}</h4>
                {s.online ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border border-emerald-300/40 bg-emerald-300/10 text-emerald-300">
                    <Wifi size={10} /> Online
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border border-rose-400/40 bg-rose-400/10 text-rose-300">
                    <WifiOff size={10} /> Offline
                  </span>
                )}
              </div>
              <div className="flex gap-4 text-[11px] text-[var(--muted)]">
                <span>Players: <span className="text-white/85">{s.player_count}/{s.max_players}</span></span>
                <span>Uptime: <span className="text-white/85">{formatUptime(s.uptime_sec)}</span></span>
                {s.fps ? <span>FPS: <span className="text-white/85">{s.fps}</span></span> : null}
              </div>
            </article>
          )) : (
            <div className="text-xs text-[var(--muted)] glass-card p-4">No servers configured.</div>
          )}
        </div>
      </section>

      {data.auto_restart?.length > 0 && (
        <section>
          <h3 className="text-sm uppercase tracking-wider text-[var(--muted)] mb-3 flex items-center gap-2">
            <Shield size={14} className="text-[var(--gold)]" />
            Auto-Restart Schedule
          </h3>
          <div className="glass-card p-3 space-y-1">
            {data.auto_restart.map((r) => (
              <div key={r.server_name} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
                <span className="text-white/85">{r.server_name}</span>
                <span className="text-[var(--muted)] text-[11px]">Every {r.interval_hours}h</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.announcements?.length > 0 && (
        <section>
          <h3 className="text-sm uppercase tracking-wider text-[var(--muted)] mb-3">
            Recent Scheduled Announcements
          </h3>
          <ul className="glass-card p-3 space-y-2">
            {data.announcements.map((a) => (
              <li key={a.id} className="text-[12px] text-white/85 py-1 border-b border-white/5 last:border-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{a.preview || "(no message)"}</span>
                  <span className="text-[10px] text-[var(--muted)] shrink-0">{relativeTime(a.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Report a Player
// ---------------------------------------------------------------------------
function ReportTab() {
  const toast = useToast();
  const [form, setForm] = useState({ reported_user: "", reason: REPORT_REASONS[0], details: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);

  const loadMine = () => api.get("/support/reports/mine")
    .then(({ data }) => setMine(data.reports || []))
    .catch(() => {});

  useEffect(() => { loadMine(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const reasonFull = form.details
      ? `${form.reason}\n\n${form.details}`
      : form.reason;
    if (!form.reported_user.trim()) {
      toast.error("Please enter the player's in-game name.");
      return;
    }
    if (form.details.trim().length < 10) {
      toast.error("Please give us at least 10 characters of detail so we can investigate.");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("reported_user", form.reported_user.trim());
    fd.append("reason", reasonFull);
    if (file) fd.append("attachment", file);
    try {
      await api.post("/support/reports", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Report submitted — admins will review it shortly.");
      setForm({ reported_user: "", reason: REPORT_REASONS[0], details: "" });
      setFile(null);
      loadMine();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="support-report-tab">
      <form onSubmit={submit} className="glass-card p-5 space-y-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1">
            Player you&apos;re reporting
          </label>
          <input
            type="text"
            className="input"
            placeholder="In-game name or Discord tag"
            value={form.reported_user}
            onChange={(e) => setForm((f) => ({ ...f, reported_user: e.target.value }))}
            maxLength={64}
            data-testid="report-reported-user"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[var(--muted)] mb-2">
            Reason
          </label>
          <div className="flex flex-wrap gap-2">
            {REPORT_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setForm((f) => ({ ...f, reason: r }))}
                className={`px-3 py-1.5 sm:py-1 rounded-full text-xs sm:text-[11px] border transition-colors ${
                  form.reason === r
                    ? "border-[var(--teal)] bg-[var(--teal)]/10 text-[var(--teal)]"
                    : "border-white/10 bg-white/5 text-[var(--muted)] hover:text-white/80"
                }`}
                data-testid={`report-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1">
            What happened? <span className="text-[var(--muted)] normal-case tracking-normal">(min 10 chars)</span>
          </label>
          <textarea
            className="input min-h-[110px] resize-y"
            placeholder="Describe when this happened, on which server, and any context you can share."
            value={form.details}
            onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
            maxLength={2000}
            data-testid="report-details"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1">
            Screenshot (optional, max 5 MB)
          </label>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block text-xs text-[var(--muted)] file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-white/10 file:text-white/85 hover:file:bg-white/15"
            data-testid="report-attachment"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="report-submit"
        >
          <Send size={14} />
          {submitting ? "Submitting…" : "Submit Report"}
        </button>
      </form>

      <section>
        <h3 className="text-sm uppercase tracking-wider text-[var(--muted)] mb-2 flex items-center gap-2">
          <Flag size={14} className="text-[var(--gold)]" />
          Your Reports
        </h3>
        {mine.length === 0 ? (
          <p className="text-xs text-[var(--muted)] glass-card p-4">You haven&apos;t submitted any reports yet.</p>
        ) : (
          <ul className="space-y-2">
            {mine.map((r) => (
              <li key={r.id} className="glass-card p-3" data-testid={`my-report-${r.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium text-white/85 truncate">
                    Report against <span className="text-[var(--teal)]">{r.reported_user}</span>
                  </span>
                  <StatusPill status={r.status} />
                </div>
                <p className="text-[11px] text-[var(--muted)] line-clamp-2 whitespace-pre-wrap">{r.reason}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[var(--muted)] uppercase tracking-wider">
                  <span>Submitted {relativeTime(r.created_at)}</span>
                  {r.resolved_at && <span>Resolved {relativeTime(r.resolved_at)}</span>}
                  {r.attachment_url && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${r.attachment_url}`}
                      target="_blank" rel="noreferrer"
                      className="text-[var(--teal)] hover:underline"
                    >
                      view screenshot
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Support Tickets
// ---------------------------------------------------------------------------
function TicketsTab() {
  const toast = useToast();
  const [tickets, setTickets] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const loadList = () => api.get("/support/tickets/mine")
    .then(({ data }) => setTickets(data.tickets || []))
    .catch((e) => toast.error(apiError(e)));

  useEffect(() => { loadList(); }, []);

  if (activeId) {
    return <TicketThread id={activeId} onBack={() => { setActiveId(null); loadList(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="support-tickets-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm uppercase tracking-wider text-[var(--muted)] flex items-center gap-2">
          <LifeBuoy size={14} className="text-[var(--teal)]" />
          Your Tickets
        </h3>
        <button
          className="btn-primary inline-flex items-center gap-1.5 text-sm"
          onClick={() => setShowNew((v) => !v)}
          data-testid="ticket-new-toggle"
        >
          {showNew ? "Cancel" : "+ New Ticket"}
        </button>
      </div>

      {showNew && (
        <NewTicketForm
          onCreated={() => { setShowNew(false); loadList(); }}
          toast={toast}
        />
      )}

      {tickets.length === 0 ? (
        <p className="text-xs text-[var(--muted)] glass-card p-4">No tickets yet &mdash; open one above if you need help.</p>
      ) : (
        <ul className="space-y-2">
          {tickets.map((t) => (
            <li
              key={t.id}
              className="glass-card p-3 cursor-pointer hover:bg-white/[0.04] transition-colors"
              onClick={() => setActiveId(t.id)}
              data-testid={`ticket-row-${t.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">#{t.id}</span>
                    <span className="text-sm font-medium text-white/85 truncate">{t.subject}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-[var(--muted)] uppercase tracking-wider mt-0.5">
                    <span>{(TICKET_CATEGORIES.find((c) => c.value === t.category)?.label || t.category)}</span>
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare size={10} /> {t.msg_count}
                    </span>
                    <span>updated {relativeTime(t.last_msg_at || t.updated_at)}</span>
                    {t.last_sender === "admin" && t.status !== "closed" && (
                      <span className="text-[var(--gold)]">↳ admin replied</span>
                    )}
                  </div>
                </div>
                <StatusPill status={t.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewTicketForm({ onCreated, toast }) {
  const [form, setForm] = useState({ category: TICKET_CATEGORIES[0].value, subject: "", body: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.subject.trim().length < 4) {
      toast.error("Subject must be at least 4 characters.");
      return;
    }
    if (form.body.trim().length < 10) {
      toast.error("Please describe the issue in at least 10 characters.");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("category", form.category);
    fd.append("subject", form.subject.trim());
    fd.append("body", form.body.trim());
    if (file) fd.append("attachment", file);
    try {
      await api.post("/support/tickets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Ticket opened — we'll respond as soon as possible.");
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="glass-card p-5 space-y-4" data-testid="new-ticket-form">
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1">Category</label>
        <select
          className="input"
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          data-testid="ticket-category"
        >
          {TICKET_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1">Subject</label>
        <input
          type="text"
          className="input"
          placeholder="Short title"
          value={form.subject}
          onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
          maxLength={120}
          data-testid="ticket-subject"
        />
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1">Description</label>
        <textarea
          className="input min-h-[120px] resize-y"
          placeholder="What's the issue? Include any error messages or steps to reproduce."
          value={form.body}
          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
          maxLength={4000}
          data-testid="ticket-body"
        />
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-[var(--muted)] mb-1">Screenshot (optional)</label>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block text-xs text-[var(--muted)] file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-white/10 file:text-white/85 hover:file:bg-white/15"
          data-testid="ticket-attachment"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="btn-primary inline-flex items-center gap-2"
        data-testid="ticket-submit"
      >
        <Send size={14} />
        {submitting ? "Opening…" : "Open Ticket"}
      </button>
    </form>
  );
}

function TicketThread({ id, onBack }) {
  const toast = useToast();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/support/tickets/${id}`)
    .then(({ data }) => setData(data))
    .catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); }, [id]);

  const reply = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    const fd = new FormData();
    fd.append("body", body.trim());
    if (file) fd.append("attachment", file);
    try {
      await api.post(`/support/tickets/${id}/reply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBody("");
      setFile(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="text-sm text-[var(--muted)]">Loading…</div>;
  const { ticket, messages } = data;
  const closed = ticket.status === "closed";

  return (
    <div className="space-y-4" data-testid={`ticket-thread-${id}`}>
      <button onClick={onBack} className="text-[11px] uppercase tracking-wider text-[var(--muted)] hover:text-white/85" data-testid="ticket-back">
        ← Back to tickets
      </button>
      <div className="glass-card p-4 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-white/90">#{ticket.id} · {ticket.subject}</h3>
          <StatusPill status={ticket.status} />
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[var(--muted)] uppercase tracking-wider">
          <span>{(TICKET_CATEGORIES.find((c) => c.value === ticket.category)?.label || ticket.category)}</span>
          <span>opened {relativeTime(ticket.created_at)}</span>
        </div>
      </div>

      <ul className="space-y-2">
        {messages.map((m) => {
          const mine = m.sender_role === "user" && String(m.sender_id) === String(user?.id);
          const isAdmin = m.sender_role === "admin";
          return (
            <li
              key={m.id}
              className={`glass-card p-3 ${isAdmin ? "border-[var(--gold)]/30" : mine ? "border-[var(--teal)]/30" : ""}`}
              data-testid={`ticket-message-${m.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className={`text-[10px] uppercase tracking-wider ${isAdmin ? "text-[var(--gold)]" : "text-[var(--muted)]"}`}>
                  {isAdmin ? "Admin" : mine ? "You" : "User"}
                </span>
                <span className="text-[10px] text-[var(--muted)]">{relativeTime(m.created_at)}</span>
              </div>
              <p className="text-[13px] text-white/90 whitespace-pre-wrap">{m.body}</p>
              {m.attachment_url && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${m.attachment_url}`}
                  target="_blank" rel="noreferrer"
                  className="inline-block mt-2 text-[11px] text-[var(--teal)] hover:underline"
                >
                  view attachment
                </a>
              )}
            </li>
          );
        })}
      </ul>

      {!closed ? (
        <form onSubmit={reply} className="glass-card p-4 space-y-3" data-testid="ticket-reply-form">
          <textarea
            className="input min-h-[80px] resize-y"
            placeholder="Type your reply…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            data-testid="ticket-reply-body"
          />
          <div className="flex items-center justify-between gap-2">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block text-xs text-[var(--muted)] file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-white/10 file:text-white/85 hover:file:bg-white/15"
              data-testid="ticket-reply-attachment"
            />
            <button
              type="submit"
              disabled={sending || !body.trim()}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="ticket-reply-submit"
            >
              <Send size={14} />
              {sending ? "Sending…" : "Send Reply"}
            </button>
          </div>
        </form>
      ) : (
        <div className="glass-card p-3 text-center text-[12px] text-[var(--muted)]">
          This ticket is closed.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root page
// ---------------------------------------------------------------------------

const TABS = [
  { id: "status",  label: "Server Status",  icon: Server,    component: StatusTab },
  { id: "report",  label: "Report a Player", icon: Flag,      component: ReportTab },
  { id: "ticket",  label: "Open a Ticket",  icon: LifeBuoy,  component: TicketsTab },
];

export default function Support() {
  const [tab, setTab] = useState("status");
  const Active = TABS.find((t) => t.id === tab)?.component || StatusTab;
  return (
    <div className="space-y-6" data-testid="support-page">
      <header>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--teal)]">
          Support
        </h1>
        <p className="text-sm text-[var(--muted)] mt-2 max-w-2xl">
          See live server status, report a player, or open a ticket. We respond
          right here on the website — no Discord required.
        </p>
      </header>

      {/* Tab strip — scrolls horizontally on mobile rather than
          wrapping to two rows, which kept tabs the same height and
          made it obvious there are more options off-screen.
          `flex-nowrap overflow-x-auto` + `whitespace-nowrap` on each
          chip + a subtle scrollbar-hint via `scrollbar-thin` keep
          the discoverability while saving vertical space. */}
      <div className="flex flex-nowrap overflow-x-auto gap-2 -mx-4 px-4 pb-1 scrollbar-thin scrollbar-thumb-white/10" data-testid="support-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`shrink-0 inline-flex items-center gap-2 px-3.5 min-h-10 py-2 rounded-full text-sm border transition-colors whitespace-nowrap ${
              tab === t.id
                ? "border-[var(--teal)] bg-[var(--teal)]/10 text-[var(--teal)]"
                : "border-white/10 bg-white/5 text-[var(--muted)] hover:text-white/80"
            }`}
            data-testid={`support-tab-${t.id}`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      <Active />
    </div>
  );
}
